import { useApiCall } from "@/app/api/apiCall";
import { MultiLevelSelect } from "@/app/components/form-fields/select/MultiLevelSelect";
import { useApiContext } from "@/app/contexts/ApiContext";
import { ISelectFieldOption } from "@/app/fields/types";
import {
  Button,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  useDisclosure,
} from "@chakra-ui/react";
import { useState } from "react";

export const AddSelectOption = ({
  fieldName,
  fieldLabel,
  onAddSuccess,
  groups,
}: {
  fieldName: string;
  fieldLabel: string;
  onAddSuccess: (value: ISelectFieldOption) => void;
  groups: string[];
}) => {
  const groupRequired = !!groups.length;

  const { isOpen, onOpen: _onOpen, onClose } = useDisclosure();
  const { createFieldOption } = useApiContext();

  const [group, setGroup] = useState<string>("");
  const [label, setLabel] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);
  const handle = useApiCall();

  const onOpen = () => {
    setLabel("");
    setGroup("");
    setIsSaving(false);
    _onOpen();
  };

  const onSubmit = async () => {
    setIsSaving(true);
    await handle(
      () =>
        createFieldOption(fieldName, {
          label,
          value: createValue(label, group),
          group: group || null,
        }),
      (res) => {
        onAddSuccess(res);
        onClose();
      },
      () => {
        setIsSaving(false);
      }
    );
  };

  return (
    <>
      If no matching value exists,&nbsp;
      <span>
        <Button
          variant="link"
          onClick={onOpen}
          className="focus:outline-none"
          color="primary.500"
          data-cy={`add-${fieldName}-option-open-modal-button`}
        >
          click here
        </Button>
      </span>
      &nbsp;to add one.
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add new {fieldLabel.toLowerCase()} value</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={10}>
            <Stack spacing={8}>
              <div>
                Please only add a new value if you are sure that there is no
                appropriate existing value.
              </div>
              {groupRequired && (
                <div data-cy={`add-${fieldName}-option-group-select`}>
                  <FormLabel>Group</FormLabel>
                  <MultiLevelSelect
                    options={groups.map((group) => ({
                      value: group,
                      label: group,
                      group: null,
                    }))}
                    isInvalid={false}
                    label={group || "Select a value..."}
                    onOptionClick={({ value }) => setGroup(value)}
                  />
                  <FormHelperText>
                    The group that this option should be part of.
                  </FormHelperText>
                </div>
              )}
              <div>
                <FormLabel>Name</FormLabel>
                <Input
                  placeholder="Enter a value..."
                  value={label}
                  data-cy={`add-${fieldName}-option-name-input`}
                  onChange={(e) => setLabel(e.target.value)}
                />
                <FormHelperText>
                  The name of the option that you would like to add.
                </FormHelperText>
              </div>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={onClose}>
              Close
            </Button>
            <Button
              variant="primary"
              mr={3}
              data-cy={`add-${fieldName}-option-save-button`}
              disabled={isSaving || !label || (groupRequired && !group)}
              onClick={onSubmit}
            >
              Submit
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

const cleanString = (input: string): string =>
  input.replace(/[^a-zA-Z0-9]/g, "_").toLowerCase();

function createValue(label: string, group: string | null): string {
  const cleanedLabel = cleanString(label);
  if (group) {
    const cleanedGroup = cleanString(group);
    return `${cleanedGroup}__${cleanedLabel}`;
  }
  return cleanedLabel;
}
